import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import PostData from "../../services/PostData.js";
import PutData from "../../services/PutData.js";
import api from "../../services/axiosInterceptor.js";
import SnackbarEx from "../Helpers/SnackbarEx.jsx";
import ConfirmDeleteDialog from "../Helpers/ConfirmDeleteDialog.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { dimensionLayoutKey } from "../Helpers/gridLayoutKeys.js";
import { fieldsFromDimensionSpec } from "../Helpers/gridFilterFields.js";
import { DimensionMultiSelect, DimensionSingleSelect, LookupField, SkuTextField } from "./TypedFields.jsx";
import {
    catalogDataGridSx,
    catalogDangerButtonSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";

function emptyForm(fields) {
    return Object.fromEntries(
        fields.map((field) => [field.key, field.type === "links" ? [] : ""])
    );
}

function linkedName(field, entry) {
    if (!entry || typeof entry !== "object") return "";
    if (field.optionFallback) {
        return [entry[field.optionLabel], entry[field.optionFallback]].filter(Boolean).join(" · ");
    }
    return entry[field.optionLabel] || entry.name || entry.internalName || "";
}

function formValue(field, raw) {
    if (field.type === "links") {
        const linked = Array.isArray(raw?.[field.key]) ? raw[field.key] : [];
        return linked.map((item) => item?._id || item).filter(Boolean);
    }
    if (field.type === "link") {
        const value = raw?.[field.key];
        return value?._id || value || "";
    }
    if (field.type === "number") {
        const value = raw?.[field.key];
        return value == null ? "" : String(value);
    }
    return raw?.[field.key] || "";
}

function cellValue(field, item) {
    if (field.type === "links") {
        const linked = Array.isArray(item?.[field.key]) ? item[field.key] : [];
        return linked.map((entry) => linkedName(field, entry)).filter(Boolean).join(", ");
    }
    if (field.type === "link") return linkedName(field, item?.[field.key]);
    if (field.type === "number") return item?.[field.key] ?? "";
    return item?.[field.key] || "";
}

function displayValue(row, column) {
    if (column.value) return column.value(row);
    const value = row?.[column.key];
    return value == null ? "" : value;
}

function LinkOptionLoader({ endpoint, onLoad }) {
    const { data } = useApiData(endpoint, { enabled: Boolean(endpoint) });
    useEffect(() => {
        onLoad(endpoint, data?.items || []);
    }, [endpoint, data, onLoad]);
    return null;
}

export default function DimensionGrid({ spec }) {
    const fields = spec.fields;
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(() => emptyForm(fields));
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [snack, setSnack] = useState({ open: false, color: "", message: "" });

    const selectedIds = useMemo(
        () => [...(selectionModel?.ids ?? [])],
        [selectionModel]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const filterFields = useMemo(() => fieldsFromDimensionSpec(spec), [spec]);
    const { apply: applyFilter, filterActive, barProps: filterBar } = useGridFilter(filterFields);

    const listUrl = useMemo(() => {
        const params = new URLSearchParams({
            page: String(filterActive ? 1 : paginationModel.page + 1),
            pageSize: String(filterActive ? 500 : paginationModel.pageSize),
        });
        if (searchQ) params.set("q", searchQ);
        return `api/${spec.endpoint}?${params.toString()}`;
    }, [paginationModel.page, paginationModel.pageSize, searchQ, spec.endpoint, filterActive]);

    const { data, error, load, isLoading, refetch } = useApiData(listUrl);

    const linkEndpoints = useMemo(() => {
        const endpoints = [];
        for (const field of fields) {
            if (
                (field.type === "links" || field.type === "link") &&
                field.optionsEndpoint &&
                !endpoints.includes(field.optionsEndpoint)
            ) {
                endpoints.push(field.optionsEndpoint);
            }
        }
        return endpoints;
    }, [fields]);
    const [itemsByEndpoint, setItemsByEndpoint] = useState({});
    const onLinkOptions = useCallback((endpoint, items) => {
        setItemsByEndpoint((prev) => (prev[endpoint] === items ? prev : { ...prev, [endpoint]: items }));
    }, []);
    const optionsFor = (field) =>
        (itemsByEndpoint[field.optionsEndpoint] || []).map((item) => ({
            _id: item._id,
            name: linkedName(field, item),
        }));

    useEffect(() => {
        const items = data?.items || [];
        setRowCount(typeof data?.total === "number" ? data.total : items.length);
        setRows(
            items.map((item) => {
                const row = { id: item._id, raw: item };
                for (const field of fields) {
                    row[field.key] = field.readOnly ? displayValue(item, field) : cellValue(field, item);
                }
                for (const column of spec.readOnly || []) {
                    row[column.key] = displayValue(item, column);
                }
                return row;
            })
        );
    }, [data, fields, spec.readOnly]);

    const filteredRows = useMemo(() => applyFilter(rows), [rows, applyFilter]);

    useEffect(() => {
        if (filterActive) {
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }
    }, [filterActive]);

    const layout = useGridLayout(dimensionLayoutKey(spec), {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = useMemo(() => {
        const editable = fields.map((field) => ({
            field: field.key,
            headerName: field.label,
            flex: 1,
            minWidth: field.readOnly ? 140 : 160,
        }));
        const locked = (spec.readOnly || []).map((column) => ({
            field: column.key,
            headerName: column.label,
            flex: 0.6,
            minWidth: 140,
        }));
        return [...editable, ...locked];
    }, [fields, spec.readOnly]);

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.committedWidths, layout.applyWidths]
    );

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm(fields));
        setDialogOpen(true);
    };

    const openEdit = (idOverride) => {
        const id = idOverride ?? selectedIds[0];
        const row = rows.find((item) => item.id === id);
        if (!row) return;
        setEditingId(id);
        setForm(
            Object.fromEntries(
                fields
                    .filter((field) => !field.readOnly)
                    .map((field) => [field.key, formValue(field, row.raw)])
            )
        );
        setDialogOpen(true);
    };

    const save = async () => {
        const required = fields.find((field) => {
            if (!field.required) return false;
            const value = form[field.key];
            if (field.readOnly) return false;
            if (field.type === "links") return !value?.length;
            if (field.type === "link") return !value;
            return !String(value || "").trim();
        });
        if (required) {
            setSnack({ open: true, color: "error", message: `${required.label} is required.` });
            return;
        }
        setSaving(true);
        const payload = Object.fromEntries(
            fields.filter((field) => !field.readOnly).map((field) => {
                if (field.type === "links") return [field.key, form[field.key] || []];
                if (field.type === "link") return [field.key, form[field.key] || null];
                if (field.type === "number") {
                    if (form[field.key] === "" || form[field.key] == null) return [field.key, null];
                    const number = Number(form[field.key]);
                    return [field.key, Number.isFinite(number) ? number : null];
                }
                return [field.key, String(form[field.key] || "").trim()];
            })
        );
        const result = editingId
            ? await PutData(payload, `${spec.endpoint}/${editingId}`)
            : await PostData(payload, spec.endpoint);
        setSaving(false);
        if (result.success) {
            setDialogOpen(false);
            setSnack({
                open: true,
                color: "success",
                message: editingId ? "Record updated." : "Record created.",
            });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } else {
            setSnack({
                open: true,
                color: "error",
                message:
                    result?.data?.message ||
                    result?.error?.response?.data?.message ||
                    "Save failed.",
            });
        }
    };

    const deleteTargets = useMemo(() => {
        const idSet = new Set(selectedIds.map(String));
        return rows
            .filter((row) => idSet.has(String(row.id)))
            .map((row) => ({
                key: String(row.id),
                label:
                    row.name ||
                    row.internalName ||
                    row.code ||
                    String(row.id),
            }));
    }, [rows, selectedIds]);

    const confirmDelete = async () => {
        if (!selectedIds.length) return;
        setDeleting(true);
        try {
            await Promise.all(selectedIds.map((id) => api.delete(`api/${spec.endpoint}/${id}`)));
            setDeleteDialogOpen(false);
            setSnack({ open: true, color: "success", message: "Deleted." });
            setSelectionModel({ type: "include", ids: new Set() });
            refetch();
        } catch (err) {
            setSnack({
                open: true,
                color: "error",
                message: err?.response?.data?.message || "Delete failed.",
            });
        } finally {
            setDeleting(false);
        }
    };

    const editingRaw = rows.find((row) => row.id === editingId)?.raw;

    return (
        <>
            {linkEndpoints.map((endpoint) => (
                <LinkOptionLoader key={endpoint} endpoint={endpoint} onLoad={onLinkOptions} />
            ))}
            {error ? (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 2 }}>
                    Failed to load records.
                </Typography>
            ) : null}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder={`Search ${spec.title.toLowerCase()}…`}
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {selectedIds.length > 0 ? (
                        <span className="catalog-selection-hint">{selectedIds.length} selected</span>
                    ) : null}
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={!selectedIds.length || deleting}
                        startIcon={
                            deleting ? (
                                <CircularProgress size={14} color="error" />
                            ) : (
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                            )
                        }
                        onClick={() => setDeleteDialogOpen(true)}
                        sx={catalogDangerButtonSx}
                    >
                        Delete
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={selectedIds.length !== 1}
                        startIcon={<EditOutlinedIcon fontSize="small" />}
                        onClick={() => openEdit()}
                        sx={catalogQuietButtonSx}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={openCreate}
                        sx={catalogPrimaryButtonSx}
                    >
                        Add
                    </Button>
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(load || isLoading)}
                        rows={filteredRows}
                        columns={columns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={setSelectionModel}
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        onRowDoubleClick={(params) => openEdit(params.id)}
                        paginationMode={filterActive ? "client" : "server"}
                        rowCount={filterActive ? filteredRows.length : rowCount}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[25, 50, 100]}
                        slotProps={appDataGridSlotProps}
                        sx={catalogDataGridSx}
                    />
                </div>
            </div>

            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth={spec.maxWidth || "xs"}
                fullWidth
                PaperProps={{ sx: { borderRadius: "14px" } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {editingId ? `Edit ${spec.singular}` : `Add ${spec.singular}`}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
                        {fields.map((field) =>
                            field.readOnly ? (
                                <LookupField
                                    key={field.key}
                                    label={field.label}
                                    value={editingRaw ? displayValue(editingRaw, field) : ""}
                                    source={field.source}
                                />
                            ) : field.type === "links" ? (
                                <DimensionMultiSelect
                                    key={field.key}
                                    label={field.label}
                                    hint={field.hint}
                                    valueIds={form[field.key] || []}
                                    options={optionsFor(field)}
                                    onChange={(ids) =>
                                        setForm((prev) => ({ ...prev, [field.key]: ids }))
                                    }
                                />
                            ) : field.type === "link" ? (
                                <DimensionSingleSelect
                                    key={field.key}
                                    label={field.label}
                                    hint={field.hint}
                                    valueId={form[field.key] || ""}
                                    options={optionsFor(field)}
                                    onChange={(id) =>
                                        setForm((prev) => ({ ...prev, [field.key]: id || "" }))
                                    }
                                />
                            ) : (
                                <SkuTextField
                                    key={field.key}
                                    label={field.label}
                                    type={field.type === "number" ? "number" : "text"}
                                    multiline={field.type === "notes"}
                                    minRows={field.type === "notes" ? 2 : undefined}
                                    required={Boolean(field.required)}
                                    value={form[field.key] ?? ""}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            [field.key]: event.target.value,
                                        }))
                                    }
                                />
                            )
                        )}
                        {editingId
                            ? (spec.readOnly || []).map((column) => (
                                  <LookupField
                                      key={column.key}
                                      label={column.label}
                                      value={displayValue(editingRaw, column)}
                                      source={column.source}
                                  />
                              ))
                            : null}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={catalogQuietButtonSx}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving}
                        onClick={save}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={catalogPrimaryButtonSx}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={confirmDelete}
                confirming={deleting}
                title={
                    deleteTargets.length === 1
                        ? `Delete ${String(spec.singular || "record").toLowerCase()}?`
                        : `Delete ${deleteTargets.length} ${String(spec.title || "records").toLowerCase()}?`
                }
                description="This cannot be undone. The following will be removed:"
                items={deleteTargets}
            />

            {snack.open ? (
                <SnackbarEx
                    color={snack.color}
                    message={snack.message}
                    open={snack.open}
                    close={() => setSnack({ open: false, color: "", message: "" })}
                />
            ) : null}
        </>
    );
}
